import { Mutation, JsonApiRequestError } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import {
	CashdeskTransaction,
	createDepositPlayerMutation,
	DepositPlayerMutationParams,
	Player,
} from 'shared/api';
import {
	greaterZeroRule,
	notEmptyRule,
	notify,
	createModalFormModel,
	ModalFormModel,
} from 'shared/lib';

export type DepositPlayerFormValue = {
	amount: string;
};

export type DepositPlayerModel = ModalFormModel<Player, DepositPlayerFormValue> & {
	depositPlayerMutation: Mutation<
		DepositPlayerMutationParams,
		CashdeskTransaction,
		JsonApiRequestError
	>;
	$pending: Store<boolean>;
};

export const createDepositPlayerModel = (): DepositPlayerModel => {
	const depositPlayerMutation = createDepositPlayerMutation();

	const model = createModalFormModel<Player, DepositPlayerFormValue>({
		fields: {
			amount: {
				init: '0',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		validateOn: ['submit'],
		resetErrorsOnChange: true,
	});

	const { $pending } = depositPlayerMutation;

	sample({
		clock: model.form.submitted,
		source: {
			values: model.form.$values,
			player: model.$modalData,
		},
		filter: ({ player }) => player !== null,
		fn: ({ player, values }) => ({
			id: player!.id,
			amount: Number(values.amount),
		}),
		target: depositPlayerMutation.start,
	});

	chain(depositPlayerMutation.finished.success, model.switcher.disable);

	notify(depositPlayerMutation.finished.success, 'deposit-player.success');

	chain(model.modalClosed, depositPlayerMutation.reset);

	return {
		depositPlayerMutation,
		$pending,
		...model,
	};
};
